/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import Button from '@mui/material/Button'
import { generate as generateUniqueId } from 'randomstring'
import { FC, useCallback, useEffect, useState } from 'react'
import { ReactSortable } from 'react-sortablejs'
import { isEqual, last } from 'lodash'
import { bulkUpdateTextFeedback, bulkUpdateTextFeedbackVariables } from '__generatedTypes__/bulkUpdateTextFeedback'
import { createTextFeedback, createTextFeedbackVariables } from '__generatedTypes__/createTextFeedback'
import {
  findAllTestimonial,
  findAllTestimonial_findAllTestimonial_items as ITestimonialInfo,
  findAllTestimonial_findAllTestimonial_items_textFeedback as TextFeedback,
  findAllTestimonialVariables,
} from '__generatedTypes__/findAllTestimonial'
import {
  CMSBoosterPages,
  CreateTextFeedbackInput,
  TestimonialWhere,
  UpdateTextFeedbackInput,
} from '__generatedTypes__/globalTypes'
import { updateTextFeedback, updateTextFeedbackVariables } from '__generatedTypes__/updateTextFeedback'
import { SORTABLE_LIST_ANIMATION } from 'components/SortableFieldArray/SortableFieldArray'
import client from 'configs/apolloClient'
import { FIND_ALL_TESTIMONIALS } from 'queries/testimonials'
import { BULK_UPDATE_TEXT_FEEDBACK, CREATE_TEXT_FEEDBACK, UPDATE_TEXT_FEEDBACK } from 'queries/textFeedback'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { toast } from 'utils/toast'
import { EMPTY_CARD_FILTERED_CLASS_NAME } from '../consts'
import TextFeedbackForm from '../TestimonialsCard/TextFeedbackForm'
import * as classes from './styles'

interface IProps {
  testimonial: ITestimonialInfo
  platformId: string
  selectedPageId: CMSBoosterPages
  platformQuery: Partial<TestimonialWhere>
}

const TestimonialsCards: FC<IProps> = ({ testimonial, selectedPageId, platformId, platformQuery }) => {
  const [emptyCards, setEmptyCards] = useState<Array<{ id: string }>>([])
  const [updateTextFeedback] = useMutation<updateTextFeedback, updateTextFeedbackVariables>(UPDATE_TEXT_FEEDBACK)
  const [createTextFeedback] = useMutation<createTextFeedback, createTextFeedbackVariables>(CREATE_TEXT_FEEDBACK)
  const [bulkUpdateTextFeedback] = useMutation<bulkUpdateTextFeedback, bulkUpdateTextFeedbackVariables>(
    BULK_UPDATE_TEXT_FEEDBACK
  )

  const [textFeedbacks, setTextFeedbacks] = useState<TextFeedback[]>(testimonial.textFeedback)
  const sortedTextFeedbacks = textFeedbacks.map((item) => ({ id: item.id, item }))

  useEffect(() => {
    setTextFeedbacks(testimonial.textFeedback)
  }, [testimonial.id, testimonial.textFeedback.length])

  useEffect(() => {
    setEmptyCards([])
  }, [testimonial.id])

  const onAddCardClick = () => {
    setEmptyCards((prevState) => [...prevState, { id: generateUniqueId() }])
  }

  const onRemoveEmptyCard = useCallback((removeCardId: string) => {
    setEmptyCards((prevState) => prevState.filter((value) => value.id !== removeCardId))
  }, [])

  const onCreateFeedback = useCallback(
    async (testimonialProps: CreateTextFeedbackInput, removeCardId: string) => {
      try {
        onRemoveEmptyCard(removeCardId)
        const { data } = await createTextFeedback({
          variables: {
            params: {
              ...testimonialProps,
            },
          },
        })
        const savedTestimonial = await client.readQuery<findAllTestimonial, findAllTestimonialVariables>({
          query: FIND_ALL_TESTIMONIALS,
          variables: {
            query: {
              where: {
                ...platformQuery,
                pageWithOrder: { page: selectedPageId },
              },
              take: TAKE_ALL_ITEMS_COUNT,
            },
          },
        })
        const newTextFeedback = data?.createTextFeedback

        let newTextFeedbacks = savedTestimonial?.findAllTestimonial.items[0]?.textFeedback || []

        if (newTextFeedback) {
          newTextFeedbacks = [...newTextFeedbacks, newTextFeedback]
        }

        await updateTestimonialCache(newTextFeedbacks)

        toast.success('Testimonial created successfully')
      } catch (error) {
        toast.error('Failed to create testimonial')
      }
    },
    [platformId, selectedPageId]
  )

  const onUpdateFeedback = useCallback(
    async (testimonialProps: UpdateTextFeedbackInput) => {
      try {
        const savedTestimonial = await client.readQuery<findAllTestimonial, findAllTestimonialVariables>({
          query: FIND_ALL_TESTIMONIALS,
          variables: {
            query: {
              where: {
                ...platformQuery,
                pageWithOrder: { page: selectedPageId },
              },
              take: TAKE_ALL_ITEMS_COUNT,
            },
          },
        })

        const { data } = await updateTextFeedback({
          variables: {
            params: {
              ...testimonialProps,
            },
          },
        })
        const newTextFeedback = data?.updateTextFeedback

        let newTextFeedbacks = savedTestimonial?.findAllTestimonial.items[0].textFeedback || []

        if (newTextFeedback) {
          newTextFeedbacks = newTextFeedbacks.filter((item) => item.id !== newTextFeedback.id)
          newTextFeedbacks.push(newTextFeedback)
          newTextFeedbacks.sort((a, b) => (a.order ?? -1) - (b.order || -1))
        }

        await updateTestimonialCache(newTextFeedbacks)

        toast.success('Testimonial updated successfully')
      } catch (error) {
        toast.error('Failed to update testimonial')
      }
    },
    [platformId, selectedPageId]
  )

  const updateTestimonialCache = async (newTextFeedbacks: TextFeedback[]) => {
    setTextFeedbacks(newTextFeedbacks)

    await client.writeQuery({
      query: FIND_ALL_TESTIMONIALS,
      variables: {
        query: {
          where: {
            ...platformQuery,
            pageWithOrder: { page: selectedPageId },
          },
          take: TAKE_ALL_ITEMS_COUNT,
        },
      },
      data: {
        findAllTestimonial: {
          __typename: 'TestimonialPaginationType',
          count: 1,
          items: [
            {
              ...testimonial,
              textFeedback: newTextFeedbacks,
            },
          ],
        },
      },
    })
  }

  // TODO: perhaps it makes sense to make a wrapper component for ReactSortable with props: items, onSort(items)
  // so as not to duplicate isEqual checks, etc.
  const handleSetList = (sortedTextFeedbacks: Array<{ id: string; item: TextFeedback }>) => {
    try {
      const textFeedbacksNew = sortedTextFeedbacks.map(({ item }) => item)

      if (isEqual(textFeedbacks, textFeedbacksNew)) {
        return
      }

      const newFeedbackOrder = textFeedbacksNew.map((feedback, order) => ({ ...feedback, order }))
      setTextFeedbacks(newFeedbackOrder)

      updateTestimonialCache(newFeedbackOrder)

      bulkUpdateTextFeedback({
        variables: {
          bulkUpdateTextFeedbackInput: textFeedbacksNew.map(({ id }, order) => ({ id, order })),
        },
      })

      toast.success('Testimonial order updated successfully')
    } catch (error) {
      toast.error('Failed to update testimonial order')
    }
  }

  const lastItemOrder = last(textFeedbacks)?.order || 0

  return (
    <>
      <ReactSortable
        list={sortedTextFeedbacks}
        setList={handleSetList}
        css={classes.grid}
        animation={SORTABLE_LIST_ANIMATION}
        filter={`.${EMPTY_CARD_FILTERED_CLASS_NAME}`}
      >
        {textFeedbacks?.map((item) => (
          <TextFeedbackForm
            selectedPageId={selectedPageId}
            textFeedbackId={item.id}
            platformQuery={platformQuery}
            lastItemOrder={lastItemOrder}
            key={item.id}
            textFeedbackInfo={item}
            testimonial={testimonial}
            onUpdateFeedback={onUpdateFeedback}
          />
        ))}
        {emptyCards.map((item) => (
          <TextFeedbackForm
            selectedPageId={selectedPageId}
            platformQuery={platformQuery}
            key={item.id}
            lastItemOrder={lastItemOrder}
            textFeedbackId={item.id}
            testimonial={testimonial}
            onRemoveEmptyCard={onRemoveEmptyCard}
            onCreateFeedback={onCreateFeedback}
          />
        ))}
      </ReactSortable>
      <Button variant="outlined" color="blue" size="large" onClick={onAddCardClick} css={classes.addButton}>
        Add More
        <AddRoundedIcon />
      </Button>
    </>
  )
}
export default TestimonialsCards
